import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useOrgDesign } from "../../hooks/useOrgDesign";
import { CardPreviewVisual } from "../CardPreviewVisual";
import type { PocketOption } from "./types";

type CardPreviewPanelProps = {
  alias: string;
  holderDisplayName: string;
  selectedPocket: PocketOption | null;
};

export const CardPreviewPanel = ({ alias, holderDisplayName, selectedPocket }: CardPreviewPanelProps) => {
  const { t } = useTranslations("cards");
  const orgDesign = useOrgDesign();
  const previewAlias = alias.trim() || t("create_card_preview_default_alias");

  return (
    <div className="grid grid-cols-1 gap-4 rounded-xl bg-muted/30 p-4 md:grid-cols-[minmax(0,320px)_1fr] md:items-center md:gap-6 lg:p-5">
      <div className="flex flex-col items-center gap-2 md:items-start">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {t("create_card_preview_label")}
        </div>
        <CardPreviewVisual
          alias={previewAlias}
          last4="••••"
          type="VIRTUAL"
          holder={holderDisplayName}
          size="md"
          className="mx-auto md:mx-0"
          design={orgDesign}
          iframeLike
        />
      </div>

      <div className="space-y-3">
        <ul className="space-y-2 text-sm">
          <li className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("card_detail_info_holder")}</span>
            <span className="ml-2 truncate font-medium text-foreground">{holderDisplayName}</span>
          </li>
          <li className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("create_card_label_alias")}</span>
            <span
              className={cn(
                "ml-2 truncate font-medium",
                alias.trim() ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {alias.trim() || "—"}
            </span>
          </li>
          <li className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("card_detail_info_pocket")}</span>
            {selectedPocket ? (
              <span className="ml-2 inline-flex items-center gap-1.5 truncate font-medium">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: selectedPocket.color }}
                  aria-hidden="true"
                />
                <span className="truncate">{selectedPocket.name}</span>
              </span>
            ) : (
              <span className="ml-2 text-muted-foreground">{t("cards_balance_own")}</span>
            )}
          </li>
          <li className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("card_detail_info_type")}</span>
            <span className="font-medium text-foreground">{t("product_virtual")}</span>
          </li>
          <li className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{t("create_card_emission_label")}</span>
            <span className="font-medium text-foreground">{t("create_card_emission_immediate")}</span>
          </li>
        </ul>

        <div className="rounded-md border border-border bg-card p-3 text-[11px] leading-snug text-muted-foreground">
          <p className="font-medium text-foreground">{t("create_card_provisioning_title")}</p>
          <p className="mt-1">{t("create_card_provisioning_body")}</p>
        </div>
      </div>
    </div>
  );
};
